import { useState } from 'react';

import { Button } from '@progress/kendo-react-buttons';
import { Typography, classNames } from '@progress/kendo-react-common';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { Card, CardBody, CardFooter, CardHeader } from '@progress/kendo-react-layout';

import { KWELLO_ICON, KwelloSvgIcon } from '../../../../common/components/icon/KwelloSvgIcon';
import { useSendFeedbackMutation } from '../../../../common/store/api/elsie.api';
import { useAppSelector } from '../../../../common/hooks/redux';
import { selectConversationId } from '../../../../common/store/slices/elsie-ai.slice';
import { useModuleProject } from '../../../../common/hooks/module';
import useApiHeaders from 'common/hooks/useApiHeaders';

interface WidgetAiMessageFeedbackProps {
  isPositive: boolean;
  onClose: () => void;
}

const REASON_MAP = {
  CONFUSING: 'Confusing',
  NOT_NEEDED: 'Not what I needed',
  INCORRECT: 'Incorrect',
  CORRECT_DATA: 'Correct Data',
  EASY: 'Easy to understand',
  COMPLETE: 'Complete',
};

export default function WidgetAiMessageFeedback({
  onClose,
  isPositive,
}: WidgetAiMessageFeedbackProps) {
  const [sendFeedback] = useSendFeedbackMutation();
  const { feedbackData } = useModuleProject();
  const conversationId =
    useAppSelector(selectConversationId) || 'a000a12a-0000-0000-aaaa-abc000000a0a';
  const apiHeaders = useApiHeaders();

  const [reason, setReason] = useState<string[]>([]);
  const [comments, setComments] = useState('');
  const feedbackRequest = {
    ...feedbackData,
    conversationId,
    rating: isPositive ? 'thumbs up' : 'thumbs down',
  };

  const onChange = (e: InputChangeEvent) => {
    setComments(e.value);
  };

  const setReasonHandler = (reason: string) => {
    setReason((state) => {
      const result = [...state];
      const index = state.indexOf(reason);

      if (index > -1) {
        result.splice(index, 1);
      } else {
        result.push(reason);
      }

      void sendFeedback({
        headers: apiHeaders,
        payload: {
          ...feedbackRequest,
          reason: result,
          comments: '',
          freeText: '',
        },
      });

      return result;
    });
  };

  const onSend = () => {
    void sendFeedback({
      headers: apiHeaders,
      payload: {
        ...feedbackRequest,
        reason,
        comments,
        freeText: '',
      },
    });

    onClose();
  };

  return (
    <Card className="widget-ai-message-template-feedback">
      <CardHeader>
        <div className="widget-ai-message-template-feedback-header">
          <Typography.h6>Why did you choose this rating? (optional)</Typography.h6>
          <Button
            fillMode="flat"
            onClick={onClose}
            className="widget-ai-message-template-feedback-close"
          >
            <KwelloSvgIcon icon={KWELLO_ICON.CANCEL_ICON} />
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        <div className="widget-ai-message-template-feedback-body-actions">
          {isPositive === false && (
            <>
              <Button
                fillMode="flat"
                onClick={() => setReasonHandler(REASON_MAP.CONFUSING)}
                className={classNames({ ['selected']: reason.indexOf(REASON_MAP.CONFUSING) > -1 })}
              >
                {reason.indexOf(REASON_MAP.CONFUSING) > -1 && (
                  <KwelloSvgIcon icon={KWELLO_ICON.CHECK_ICON} />
                )}
                {REASON_MAP.CONFUSING}
              </Button>
              <Button
                fillMode="flat"
                onClick={() => setReasonHandler(REASON_MAP.NOT_NEEDED)}
                className={classNames({
                  ['selected']: reason.indexOf(REASON_MAP.NOT_NEEDED) > -1,
                })}
              >
                {reason.indexOf(REASON_MAP.NOT_NEEDED) > -1 && (
                  <KwelloSvgIcon icon={KWELLO_ICON.CHECK_ICON} />
                )}
                {REASON_MAP.NOT_NEEDED}
              </Button>
              <Button
                fillMode="flat"
                onClick={() => setReasonHandler(REASON_MAP.INCORRECT)}
                className={classNames({ ['selected']: reason.indexOf(REASON_MAP.INCORRECT) > -1 })}
              >
                {reason.indexOf(REASON_MAP.INCORRECT) > -1 && (
                  <KwelloSvgIcon icon={KWELLO_ICON.CHECK_ICON} />
                )}
                {REASON_MAP.INCORRECT}
              </Button>
            </>
          )}
          {isPositive === true && (
            <>
              <Button
                fillMode="flat"
                onClick={() => setReasonHandler(REASON_MAP.CORRECT_DATA)}
                className={classNames({
                  ['selected']: reason.indexOf(REASON_MAP.CORRECT_DATA) > -1,
                })}
              >
                {reason.indexOf(REASON_MAP.CORRECT_DATA) > -1 && (
                  <KwelloSvgIcon icon={KWELLO_ICON.CHECK_ICON} />
                )}
                {REASON_MAP.CORRECT_DATA}
              </Button>
              <Button
                fillMode="flat"
                onClick={() => setReasonHandler(REASON_MAP.EASY)}
                className={classNames({ ['selected']: reason.indexOf(REASON_MAP.EASY) > -1 })}
              >
                {reason.indexOf(REASON_MAP.EASY) > -1 && (
                  <KwelloSvgIcon icon={KWELLO_ICON.CHECK_ICON} />
                )}
                {REASON_MAP.EASY}
              </Button>
              <Button
                fillMode="flat"
                onClick={() => setReasonHandler(REASON_MAP.COMPLETE)}
                className={classNames({ ['selected']: reason.indexOf(REASON_MAP.COMPLETE) > -1 })}
              >
                {reason.indexOf(REASON_MAP.COMPLETE) > -1 && (
                  <KwelloSvgIcon icon={KWELLO_ICON.CHECK_ICON} />
                )}
                {REASON_MAP.COMPLETE}
              </Button>
            </>
          )}
        </div>
        <Input placeholder="Offer more comments" onChange={onChange} autoFocus={true} />
        <Typography.p>
          Help us improve ElsieAI's responses. Your feedback will help us refine and enhance the
          ElsieAI experience.
        </Typography.p>
      </CardBody>
      <CardFooter>
        <Button
          onClick={onSend}
          size="large"
          themeColor="secondary"
          disabled={!reason.length && !comments.length}
        >
          Send
        </Button>
      </CardFooter>
    </Card>
  );
}
